import React from 'react';
import { FormModal } from 'components/common';
import { ValidationErrors } from 'constants/validations';
import { SHIPPING_TYPE_OPTIONS } from 'models/PurchaseOrder';
import { Col, Form, InputNumber, Radio, Row, Select } from 'components';
import { useShippingEdit } from './useShippingEdit';
import { Alert, Typography } from 'antd';
import { ErrorExclamationMarkIcon } from 'components/common/icons';

const {Item} = Form;
const {Text} = Typography;

export interface IShippingUpdate {
  onCancel: () => void;
  open: boolean;
}

const style: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
export const ShippingEditModal = React.memo((props: IShippingUpdate) => {
  const {onCancel: onCancelModal, open} = props;
  const {
    form,
    isLoadingUpdate,
    onFinish,
    onCancel,
    warehouseData,
    clientShippingInfo,
    initialShipping,
    disableSaveBtn,
    onValuesChange,
  } = useShippingEdit({
    onCancelModal,
    open,
  });
  return (
    <FormModal
      form={form}
      title={'Update Shipping Information'}
      okText={'Save'}
      open={open}
      width={680}
      isLoading={isLoadingUpdate}
      onCancel={onCancel}
      onSubmit={onFinish}
      disableOkBtn={disableSaveBtn}
      onValuesChange={onValuesChange}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Item
            label="Shipping Type*"
            name="shippingType"
            rules={[{required: true, message: ValidationErrors.required('Shipping Type')}]}
          >
            <Select size="large" options={SHIPPING_TYPE_OPTIONS} placeholder="Select shipping type"/>
          </Item>
        </Col>
        <Col span={12}>
          <Item
            label="Shipping Cost (€)*"
            name="shippingCost"
            rules={[{required: true, message: ValidationErrors.required('Shipping Cost')}]}
          >
            <InputNumber
              size="large"
              style={{width: '100%'}}
              min={0}
              precision={2}
              placeholder="0.00"
              addonAfter="€"
            />
          </Item>
        </Col>
        <Col span={24}>
          <Form.Item name="toWarehouse" initialValue={initialShipping.toWarehouse}>
            <Radio.Group
              className="scribex-erp-warehouse-radio-button"
              style={style}
              value={form.getFieldValue('toWarehouse')}
            >
              <Form.Item shouldUpdate noStyle>
                {({getFieldValue}) => {
                  const current = getFieldValue('toWarehouse');
                  return (
                    <Row className={'warehouse-radio-button'} gutter={[0, 10]}>
                      <Col span={24}>
                        <Radio value={true}>
                          <Text className="font-weight-500 font-size-16">Delivery To Warehouse</Text>
                        </Radio>
                      </Col>
                      {current && (
                        <Col span={24}>
                          <Form.Item
                            label="Delivery To Warehouse*"
                            name="countryWarehouseId"
                            rules={[{required: true, message: ValidationErrors.required('Warehouse')}]}
                          >
                            <Select options={warehouseData} size="large"/>
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  );
                }}
              </Form.Item>
              <Form.Item shouldUpdate noStyle>
                {({getFieldValue}) => {
                  const current = getFieldValue('toWarehouse');
                  const {name, country, state, city, streetAddress, zip} = clientShippingInfo?.shippingAddress || {};
                  const clientShippingAddressLabel = [name, country?.name, state, city, streetAddress, zip]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <Row className={'warehouse-radio-button'} gutter={[0, 10]}>
                      <Col span={24}>
                        <Radio value={false}>
                          <Text className="font-weight-500 font-size-16">Ship direct to Client</Text>
                        </Radio>
                      </Col>
                      <Col>
                        {!current && clientShippingAddressLabel && (
                          <Col span={24}>
                            <Text type="secondary">{clientShippingAddressLabel}</Text>
                          </Col>
                        )}
                        {!current && !clientShippingInfo?.clientShippingAddress && clientShippingInfo?.errorType && (
                          <Col span={24}>
                            <Alert
                              message={`${clientShippingInfo?.errorType === 'SELF_PICKUP' ? 'EXW (self-pickup) delivery option is selected for some items in this order. Direct delivery to the client is not available under this option.' : 'Multiple addresses found in this order. Direct shipping is possible only for a single client address.'}`}
                              type="error"
                              showIcon
                              banner
                              style={{alignItems: 'start', gap: '8px'}}
                              icon={<ErrorExclamationMarkIcon/>}
                            />
                          </Col>
                        )}
                      </Col>
                    </Row>
                  );
                }}
              </Form.Item>
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </FormModal>
  );
});
